import { useEffect, useMemo, useState } from "react";
import {
  CalendarBlank,
  CalendarPlus,
  CheckCircle,
  Clock,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  cancelStaffTimeOffRequest,
  createStaffTimeOffRequest,
  formatTimeOffAmount,
  timeOffStatusLabel,
} from "@/lib/scheduling";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const EMPTY_LIST = Object.freeze([]);

/**
 * Teacher mobile surface for a center-configured time-off workflow.
 *
 * The request amount is intentionally entered rather than inferred from a
 * shift or clock entry. A request is not an approval, and this panel never
 * claims paid time, eligibility, accrual, or payroll status.
 */
export default function TeacherTimeOffPanel({ snapshot, loading, error, onRefresh }) {
  const [requestOpen, setRequestOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const policies = snapshot?.policies || EMPTY_LIST;
  const balances = snapshot?.balances || EMPTY_LIST;
  const requests = snapshot?.requests || EMPTY_LIST;
  const balancesByPolicy = useMemo(
    () => new Map(balances.map((balance) => [balance.policyId, balance])),
    [balances],
  );
  const pendingCount = requests.filter((request) => request.status === "pending").length;

  async function cancelPendingRequest(request) {
    if (!request?.id || request.status !== "pending") return;
    setCancellingId(request.id);
    try {
      await cancelStaffTimeOffRequest({ requestId: request.id });
      toast.success("Time-off request cancelled");
      await onRefresh?.();
    } catch (err) {
      toast.error(err?.message || "Could not cancel this request");
    } finally {
      setCancellingId(null);
    }
  }

  if (loading) {
    return (
      <section data-testid="teacher-time-off-loading" className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="h-4 w-24 animate-pulse rounded bg-neutral-100" />
        <div className="mt-3 h-16 animate-pulse rounded-xl bg-neutral-50" />
      </section>
    );
  }

  if (error) {
    return (
      <section data-testid="teacher-time-off-unavailable" className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-4">
        <div className="flex gap-3">
          <WarningCircle size={20} weight="duotone" className="mt-0.5 shrink-0 text-neutral-500" />
          <div>
            <p className="text-sm font-semibold text-neutral-900">Time off is not ready yet</p>
            <p className="mt-1 text-xs text-neutral-600">Your schedule is still available. Try again after your center’s update finishes.</p>
            <button type="button" onClick={() => onRefresh?.()} className="mt-3 text-xs font-semibold text-primary-hover underline underline-offset-2">
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (snapshot?.status === "not_configured" || policies.length === 0) {
    return (
      <section data-testid="teacher-time-off-not-configured" className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-4">
        <div className="flex gap-3">
          <CalendarBlank size={20} weight="duotone" className="mt-0.5 shrink-0 text-neutral-500" />
          <div>
            <p className="text-sm font-semibold text-neutral-900">Time off is not configured</p>
            <p className="mt-1 text-xs leading-5 text-neutral-600">
              Your center has not added a time-off category yet. Ask an administrator before making a request.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="teacher-time-off" className="rounded-2xl border border-neutral-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Your availability</p>
          <h2 className="mt-1 font-heading text-lg font-semibold text-neutral-900">Time off</h2>
          <p className="mt-1 max-w-xl text-xs leading-5 text-neutral-600">
            Send a request for your administrator to review. A request does not change your schedule automatically.
          </p>
        </div>
        <button
          type="button"
          data-testid="teacher-time-off-request-open"
          onClick={() => setRequestOpen(true)}
          className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-primary-hover active:scale-[0.98]"
        >
          <CalendarPlus size={16} weight="duotone" />
          Request time off
        </button>
      </div>

      <TimeOffBalanceStrip policies={policies} balancesByPolicy={balancesByPolicy} />

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Requests</p>
        {pendingCount > 0 && (
          <span data-testid="teacher-time-off-pending-count" className="rounded-full bg-[#C6851E]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A5A12]">
            {pendingCount} awaiting review
          </span>
        )}
      </div>

      {requests.length === 0 ? (
        <div data-testid="teacher-time-off-empty" className="mt-3 rounded-xl bg-neutral-50 px-3 py-4 text-center">
          <p className="text-sm font-medium text-neutral-800">No requests yet</p>
          <p className="mt-1 text-xs text-neutral-500">Choose a center category and send the dates you need away.</p>
        </div>
      ) : (
        <ol data-testid="teacher-time-off-request-list" className="mt-3 space-y-2">
          {requests.map((request) => (
            <li key={request.id} data-testid={`teacher-time-off-request-${request.id}`} className="rounded-xl border border-neutral-100 bg-neutral-50 px-3 py-3">
              <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900">{request.policyLabel}</p>
                  <p className="mt-0.5 text-xs text-neutral-600">
                    {formatDateRange(request.startsAt, request.endsAt)} · {formatTimeOffAmount(request.requestedAmount, request.balanceUnit)}
                  </p>
                </div>
                <StatusChip status={request.status} />
              </div>
              {request.requestNote && <p className="mt-2 text-xs leading-5 text-neutral-600">{request.requestNote}</p>}
              {request.decisionNote && <p className="mt-2 rounded-lg bg-white px-2.5 py-2 text-xs leading-5 text-neutral-600">{request.decisionNote}</p>}
              {request.cancelNote && <p className="mt-2 rounded-lg bg-white px-2.5 py-2 text-xs leading-5 text-neutral-600">{request.cancelNote}</p>}
              {request.scheduledShiftConflictCount > 0 && request.status !== "cancelled" && (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#8A5A12]">
                  <WarningCircle size={14} weight="duotone" />
                  Your center will coordinate coverage for {request.scheduledShiftConflictCount} planned shift{request.scheduledShiftConflictCount === 1 ? "" : "s"}.
                </p>
              )}
              {request.status === "pending" && (
                <button
                  type="button"
                  data-testid={`teacher-time-off-cancel-${request.id}`}
                  disabled={cancellingId === request.id}
                  onClick={() => cancelPendingRequest(request)}
                  className="mt-3 inline-flex min-h-9 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 disabled:opacity-60"
                >
                  <X size={14} weight="bold" />
                  {cancellingId === request.id ? "Cancelling…" : "Cancel request"}
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      <TimeOffRequestSheet
        open={requestOpen}
        policies={policies}
        onOpenChange={setRequestOpen}
        onSaved={async () => {
          setRequestOpen(false);
          await onRefresh?.();
        }}
      />
    </section>
  );
}

function TimeOffBalanceStrip({ policies, balancesByPolicy }) {
  const tracked = policies.filter((policy) => policy.tracksBalance);
  if (tracked.length === 0) return null;

  return (
    <div data-testid="teacher-time-off-balances" className="mt-4 grid gap-2 sm:grid-cols-2">
      {tracked.map((policy) => {
        const balance = balancesByPolicy.get(policy.id);
        const hasRecord = (balance?.balanceEntryCount || 0) > 0;
        return (
          <div key={policy.id} className="rounded-xl border border-primary/10 bg-primary/5 px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary-hover">{policy.label}</p>
            <p className="mt-1 text-sm font-semibold text-neutral-900">
              {hasRecord ? formatTimeOffAmount(balance.balanceAmount, policy.balanceUnit) : "Balance not recorded"}
            </p>
            <p className="mt-0.5 text-[11px] text-neutral-600">Your center manages this balance. It does not decide approval automatically.</p>
          </div>
        );
      })}
    </div>
  );
}

function TimeOffRequestSheet({ open, policies, onOpenChange, onSaved }) {
  const [policyId, setPolicyId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [requestNote, setRequestNote] = useState("");
  const [saving, setSaving] = useState(false);
  const selectedPolicy = policies.find((policy) => policy.id === policyId) || policies[0] || null;

  useEffect(() => {
    if (!open) return;
    const start = roundToHour(new Date(Date.now() + 60 * 60 * 1000));
    const end = new Date(start);
    end.setHours(end.getHours() + 8);
    setPolicyId((current) => current || policies[0]?.id || "");
    setStartsAt((current) => current || toDateTimeInputValue(start));
    setEndsAt((current) => current || toDateTimeInputValue(end));
    setRequestedAmount("");
    setRequestNote("");
  }, [open, policies]);

  async function submit(event) {
    event.preventDefault();
    const start = new Date(startsAt);
    const end = new Date(endsAt);
    const amount = Number(requestedAmount);
    if (!selectedPolicy || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start || !Number.isFinite(amount) || amount <= 0) {
      toast.error("Choose a category, valid time window, and requested amount.");
      return;
    }

    setSaving(true);
    try {
      await createStaffTimeOffRequest({
        policyId: selectedPolicy.id,
        startsAt: start.toISOString(),
        endsAt: end.toISOString(),
        requestedAmount: amount,
        requestNote: requestNote.trim() || null,
      });
      toast.success("Time-off request sent for review");
      await onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Could not send this request");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-3xl" data-testid="teacher-time-off-request-sheet">
        <SheetHeader className="text-left">
          <SheetTitle>Request time off</SheetTitle>
          <SheetDescription>
            Choose the time and amount your center uses. Your administrator will review the request before anything changes.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <label className="block">
            <span className="text-sm font-semibold text-neutral-800">Category</span>
            <select
              data-testid="teacher-time-off-policy"
              value={selectedPolicy?.id || ""}
              onChange={(event) => setPolicyId(event.target.value)}
              className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              required
            >
              {policies.map((policy) => <option key={policy.id} value={policy.id}>{policy.label}</option>)}
            </select>
          </label>
          {selectedPolicy?.description && <p className="-mt-2 text-xs leading-5 text-neutral-500">{selectedPolicy.description}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-semibold text-neutral-800">Starts</span>
              <input data-testid="teacher-time-off-starts" type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} required className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-neutral-800">Ends</span>
              <input data-testid="teacher-time-off-ends" type="datetime-local" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} required className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-semibold text-neutral-800">Requested amount ({selectedPolicy?.balanceUnit || "hours"})</span>
            <input data-testid="teacher-time-off-amount" type="number" min="0.01" step="0.25" inputMode="decimal" value={requestedAmount} onChange={(event) => setRequestedAmount(event.target.value)} required className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            <span className="mt-1 block text-xs leading-5 text-neutral-500">Enter the amount your center expects for this category. HoneyAcorn does not calculate it from your shift.</span>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-neutral-800">Note <span className="font-normal text-neutral-500">(optional)</span></span>
            <textarea data-testid="teacher-time-off-note" value={requestNote} onChange={(event) => setRequestNote(event.target.value)} maxLength={2000} rows={3} className="mt-1.5 w-full resize-y rounded-xl border border-neutral-200 px-3 py-2 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </label>
          <button data-testid="teacher-time-off-submit" type="submit" disabled={saving} className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">
            <CalendarPlus size={17} weight="duotone" />
            {saving ? "Sending…" : "Send for review"}
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function StatusChip({ status }) {
  const tone = {
    approved: "bg-primary/10 text-primary-hover",
    declined: "bg-red-50 text-red-700",
    cancelled: "bg-neutral-200 text-neutral-600",
    pending: "bg-[#C6851E]/10 text-[#8A5A12]",
  }[status] || "bg-neutral-100 text-neutral-600";
  const Icon = status === "approved" ? CheckCircle : status === "pending" ? Clock : CalendarBlank;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}><Icon size={12} weight="duotone" />{timeOffStatusLabel(status)}</span>;
}

function formatDateRange(startsAt, endsAt) {
  if (!startsAt || !endsAt) return "Dates to be confirmed";
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const date = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
  const time = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
  if (start.toDateString() === end.toDateString()) return `${date.format(start)} · ${time.format(start)}–${time.format(end)}`;
  return `${date.format(start)} ${time.format(start)} – ${date.format(end)} ${time.format(end)}`;
}

function toDateTimeInputValue(date) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function roundToHour(date) {
  const next = new Date(date);
  next.setMinutes(0, 0, 0);
  if (next <= date) next.setHours(next.getHours() + 1);
  return next;
}
